import { createAsyncThunk } from "@reduxjs/toolkit";
import moment from "moment";
import {
  exportLeadsToCSV,
  exportLeadsToExcel,
} from "../../../../helper/leadExcelExport";
import { getLeadsPage } from "../../../../helper/leadResponse";
import apiService from "../../../../services/apiService/api/leadManagementApi";

const LEAD_MANAGEMENT_FLOW_SLICE_NAME = "leadManagement";

// Business rejections (duplicate lead, validity window, ...) come back on an
// HTTP 200 as `{ status: "ERROR", data: { errorMessage } }`, so `request` never
// rejects and the thunk would otherwise report them as a success. Pull the
// message out here and let the caller reject with it.
const getEnvelopeError = (response) => {
  try {
    const body =
      typeof response?.data === "string"
        ? JSON.parse(response.data)
        : response?.data;
    if (body?.status && String(body.status).toUpperCase() === "ERROR") {
      return body?.data?.errorMessage || "Something Went Wrong";
    }
  } catch (e) {
    return "Something Went Wrong";
  }
  return null;
};

const leadManagementThunkActions = {
  getCustomerData: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/getCustomerData`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.getCustomerData(payload);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  getCategoryBrandData: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/getCategoryBrandData`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.getCategoryBrandData(payload);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  getReasons: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/getReasons`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.getReasons(payload);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  getDownloadData: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/getDownloadData`,
    async (payload, { rejectWithValue }) => {
      try {
        // Pull out the client-only format; the rest (incl. year + month)
        // are forwarded to the API, which filters server-side.
        const { format = "xlsx", ...apiPayload } = payload;

        const result = await apiService.getDownloadData({
          ...apiPayload,
          page: 0,
          size: apiPayload.size || 2000,
        });

        // Same envelope as the list endpoint: the page sits under `data.leads`.
        const firstPage = getLeadsPage(JSON.parse(result.data)?.data);
        const totalPages = firstPage?.totalPages || 0;
        let allData = [];
        if (firstPage?.content?.length > 0) {
          allData = [...allData, ...firstPage.content];
        }
        for (let page = 1; page < totalPages; page++) {
          const response = await apiService.getDownloadData({
            ...apiPayload,
            page: page,
            size: apiPayload.pageSize || 2000,
          });
          const nextPage = getLeadsPage(JSON.parse(response.data)?.data);

          if (nextPage?.content?.length > 0) {
            allData = [...allData, ...nextPage.content];
          }
        }

        const filename = `LeadsData_${moment().format("D-MM-YYYY_hh-mm-ss")}`;
        if (format === "csv") {
          exportLeadsToCSV(allData, `${filename}.csv`);
        } else {
          await exportLeadsToExcel(allData, `${filename}.xlsx`);
        }

        return { success: true };
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  getViewLeadsData: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/getViewLeadsData`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.getViewLeadsData(payload);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  createLead: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/createLead`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.createLead(payload);
        const envelopeError = getEnvelopeError(response);
        if (envelopeError) return rejectWithValue(envelopeError);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  updateLead: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/updateLead`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.updateLead(payload);
        const envelopeError = getEnvelopeError(response);
        if (envelopeError) return rejectWithValue(envelopeError);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  getAttributes: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/getAttributes`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.getAttributes(payload);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
  getStatus: createAsyncThunk(
    `${LEAD_MANAGEMENT_FLOW_SLICE_NAME}/getStatus`,
    async (payload, { rejectWithValue }) => {
      try {
        const response = await apiService.getStatus(payload);
        return response || {};
      } catch (err) {
        return rejectWithValue(err);
      }
    }
  ),
};

export default leadManagementThunkActions;
